import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { proxyPhoto } from '../utils/photo';

// "Did we miss one?" — manual rescue + teaching flow for MLS Opportunities.
// State machine:
//   address  → looking_up
//                ↓ found             ↓ not_found
//                feedback            offer_offmarket
//                ↓ saving
//                done | error
export default function MissedOpportunityModal({ onClose, onSaved }) {
  const navigate = useNavigate();
  const [step, setStep] = useState('address'); // address|looking_up|feedback|offer_offmarket|saving|done|error
  const [address, setAddress] = useState('');
  const [zip, setZip] = useState('');
  const [error, setError] = useState(null);

  // Lookup result
  const [listing, setListing] = useState(null);
  const [rawMls, setRawMls] = useState(null);
  const [source, setSource] = useState(null);          // 'local'|'mls_grid'
  const [wasFilteredOut, setWasFilteredOut] = useState(false);
  const [alreadyOpp, setAlreadyOpp] = useState(false);

  // Feedback
  const [userText, setUserText] = useState('');
  const [result, setResult] = useState(null);

  // Typeahead — debounced suggestions from /api/listings/address-suggest
  const [suggestions, setSuggestions]   = useState([]);
  const [suggestLoading, setSuggestLoading] = useState(false);
  const [suggestOpen, setSuggestOpen]   = useState(false);
  const [activeIdx, setActiveIdx]       = useState(-1);
  const debounceRef = useRef(null);
  const lastReqRef  = useRef(0);

  useEffect(() => {
    // Debounce: 250ms after the last keystroke, fire one request. Race-guard with a
    // monotonic request id so a slow earlier response can't overwrite a fresh one.
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const q = address.trim();
    if (q.length < 3) { setSuggestions([]); setSuggestOpen(false); return; }
    debounceRef.current = setTimeout(async () => {
      const myId = ++lastReqRef.current;
      setSuggestLoading(true);
      try {
        const r = await fetch(`/api/listings/address-suggest?q=${encodeURIComponent(q)}&limit=8`);
        const data = await r.json();
        if (myId !== lastReqRef.current) return; // stale
        setSuggestions(data.suggestions || []);
        setSuggestOpen((data.suggestions || []).length > 0);
        setActiveIdx(-1);
      } catch {
        if (myId === lastReqRef.current) { setSuggestions([]); setSuggestOpen(false); }
      } finally {
        if (myId === lastReqRef.current) setSuggestLoading(false);
      }
    }, 250);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [address]);

  // User picked a suggestion — fast path. Sends listing_key so the backend skips
  // the fuzzy match and returns the exact row.
  async function handlePickSuggestion(s) {
    setAddress(s.address);
    setZip(s.zip || '');
    setSuggestOpen(false);
    setStep('looking_up');
    setError(null);
    try {
      const r = await fetch('/api/listings/lookup-by-address', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ listing_key: s.listing_key, address: s.address, zip: s.zip || undefined }),
      });
      const data = await r.json();
      if (data.error) throw new Error(data.error);
      if (!data.found) { setStep('offer_offmarket'); return; }
      setListing(data.listing);
      setRawMls(data.raw_mls || null);
      setSource(data.source);
      setWasFilteredOut(!!data.was_filtered_out);
      setAlreadyOpp(!!data.already_an_opportunity);
      setStep('feedback');
    } catch (err) {
      setError(err.message);
      setStep('error');
    }
  }

  async function handleLookup() {
    if (!address.trim()) return;
    setSuggestOpen(false);
    setStep('looking_up');
    setError(null);
    try {
      const r = await fetch('/api/listings/lookup-by-address', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ address: address.trim(), zip: zip.trim() || undefined }),
      });
      const data = await r.json();
      if (data.error) throw new Error(data.error);
      if (!data.found) {
        setStep('offer_offmarket');
        return;
      }
      setListing(data.listing);
      setRawMls(data.raw_mls || null);
      setSource(data.source);
      setWasFilteredOut(!!data.was_filtered_out);
      setAlreadyOpp(!!data.already_an_opportunity);
      setStep('feedback');
    } catch (err) {
      setError(err.message);
      setStep('error');
    }
  }

  function handleAddressKey(e) {
    if (!suggestOpen || suggestions.length === 0) {
      if (e.key === 'Enter') handleLookup();
      return;
    }
    if (e.key === 'ArrowDown') { e.preventDefault(); setActiveIdx(i => Math.min(i + 1, suggestions.length - 1)); }
    else if (e.key === 'ArrowUp')   { e.preventDefault(); setActiveIdx(i => Math.max(i - 1, -1)); }
    else if (e.key === 'Enter') {
      e.preventDefault();
      if (activeIdx >= 0 && suggestions[activeIdx]) handlePickSuggestion(suggestions[activeIdx]);
      else handleLookup();
    }
    else if (e.key === 'Escape') setSuggestOpen(false);
  }

  async function handleSave() {
    if (!userText.trim()) return;
    setStep('saving');
    setError(null);
    try {
      const r = await fetch('/api/listings/missed-opportunity', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ listing, raw_mls: rawMls, user_text: userText.trim() }),
      });
      const data = await r.json();
      if (data.error) throw new Error(data.error);
      setResult(data);
      setStep('done');
      if (onSaved) onSaved(data);
    } catch (err) {
      setError(err.message);
      setStep('error');
    }
  }

  function goOffMarket() {
    onClose && onClose();
    navigate('/off-market');
  }

  return (
    <div style={overlay}>
      <div style={modalWrap}>
        <div style={header}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--white)' }}>Did we miss one?</div>
            <div style={{ fontSize: 11, color: 'var(--gray-500)', marginTop: 2 }}>
              Add a listing the system filtered out — and teach it what to catch next time.
            </div>
          </div>
          <button onClick={onClose} style={closeBtn}>×</button>
        </div>

        <div style={body}>
          {step === 'address' && (
            <>
              <Field label="Property Address" required>
                <div style={{ position: 'relative' }}>
                  <input
                    value={address}
                    onChange={e => setAddress(e.target.value)}
                    onKeyDown={handleAddressKey}
                    onFocus={() => suggestions.length > 0 && setSuggestOpen(true)}
                    onBlur={() => setTimeout(() => setSuggestOpen(false), 150)}
                    placeholder="Start typing — e.g. 6204 Shadow"
                    style={input}
                    autoComplete="off"
                    autoFocus
                  />
                  {suggestLoading && (
                    <div style={{ position: 'absolute', right: 10, top: 9, width: 12, height: 12, border: '2px solid var(--navy-border)', borderTopColor: 'var(--teal)', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
                  )}
                  {suggestOpen && suggestions.length > 0 && (
                    <div style={dropdown}>
                      {suggestions.map((s, i) => (
                        <div
                          key={s.listing_key}
                          onMouseDown={(e) => { e.preventDefault(); handlePickSuggestion(s); }}
                          onMouseEnter={() => setActiveIdx(i)}
                          style={{
                            ...dropdownItem,
                            background: i === activeIdx ? 'rgba(20, 80, 74,0.12)' : 'transparent',
                          }}
                        >
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8 }}>
                            <div style={{ fontSize: 12, color: 'var(--white)', fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {s.address}
                            </div>
                            <SuggestionBadge badge={s.badge} />
                          </div>
                          <div style={{ fontSize: 10, color: 'var(--gray-500)', marginTop: 2 }}>
                            {[s.city, s.zip, s.beds ? `${s.beds}bd` : null, s.sqft ? `${s.sqft}sf` : null, s.year_built ? `'${String(s.year_built).slice(2)}` : null, s.list_price ? `$${Math.round(s.list_price/1000)}k` : null]
                              .filter(Boolean).join(' • ')}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </Field>
              <Field label="Zip (optional, narrows search)">
                <input
                  value={zip}
                  onChange={e => setZip(e.target.value.replace(/[^0-9]/g, '').slice(0, 5))}
                  onKeyDown={e => e.key === 'Enter' && handleLookup()}
                  placeholder="78745"
                  style={input}
                />
              </Field>
              <div style={hintBox}>
                As you type, we'll suggest matching MLS addresses. Pick one to skip ahead, or hit
                "Find Listing" with whatever you typed. Suggestions tagged{' '}
                <span style={{ color: '#B85450', fontWeight: 700 }}>Filtered Out</span> are the
                ones we wrongly rejected — those are the most valuable to teach us about.
              </div>
              <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
                <button onClick={onClose} style={ghostBtn}>Cancel</button>
                <button
                  onClick={handleLookup}
                  disabled={!address.trim()}
                  style={{ ...primaryBtn, flex: 2, opacity: !address.trim() ? 0.5 : 1 }}
                >
                  Find Listing
                </button>
              </div>
            </>
          )}

          {step === 'looking_up' && <Spinner label="Looking up the listing…" />}

          {step === 'feedback' && listing && (
            <>
              <div style={listingCard}>
                {listing.photo_url && (
                  <img
                    src={proxyPhoto(listing.photo_url)}
                    alt=""
                    style={{ width: 92, height: 70, objectFit: 'cover', borderRadius: 6, flexShrink: 0 }}
                    onError={e => { e.target.style.display = 'none'; }}
                  />
                )}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--white)', marginBottom: 2 }}>
                    {listing.address || '(no address)'}
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--gray-500)' }}>
                    {[
                      listing.city,
                      listing.zip,
                      listing.beds ? `${listing.beds}bd` : null,
                      listing.baths ? `${listing.baths}ba` : null,
                      listing.sqft ? `${listing.sqft.toLocaleString()} sf` : null,
                      listing.year_built ? `built ${listing.year_built}` : null,
                    ].filter(Boolean).join(' • ')}
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--teal)', marginTop: 4, fontWeight: 700 }}>
                    {listing.list_price ? `$${listing.list_price.toLocaleString()}` : 'No price'}
                    <span style={{ color: 'var(--gray-500)', fontWeight: 400, marginLeft: 8 }}>
                      · source: {source === 'local' ? 'local cache' : 'MLS Grid'}
                    </span>
                  </div>
                  {wasFilteredOut && (
                    <div style={{ marginTop: 6, fontSize: 11, color: '#B85450' }}>
                      ⚠ This listing was previously auto-filtered out. Your feedback will rescue it
                      and teach the scorer.
                    </div>
                  )}
                  {alreadyOpp && (
                    <div style={{ marginTop: 6, fontSize: 11, color: '#4A7C59' }}>
                      ✓ Already in opportunities — your feedback will still strengthen the scorer.
                    </div>
                  )}
                </div>
              </div>

              <Field label="Why is this a deal? — help us catch listings like this next time" required>
                <textarea
                  value={userText}
                  onChange={e => setUserText(e.target.value)}
                  placeholder="e.g. Estate sale, original kitchen, big corner lot under $300k. Owner clearly motivated — listed 60+ days, two price cuts."
                  rows={5}
                  style={{ ...input, resize: 'vertical', fontFamily: 'inherit', lineHeight: 1.5 }}
                  autoFocus
                />
              </Field>

              <div style={hintBox}>
                We'll extract keywords from your explanation and use them to (a) rescue similar
                listings the scorer already filtered out, and (b) bias future MLS syncs to weight
                this kind of deal positively.
              </div>

              <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
                <button onClick={() => setStep('address')} style={ghostBtn}>Back</button>
                <button
                  onClick={handleSave}
                  disabled={!userText.trim()}
                  style={{ ...primaryBtn, flex: 2, opacity: !userText.trim() ? 0.5 : 1 }}
                >
                  Save &amp; Teach
                </button>
              </div>
            </>
          )}

          {step === 'offer_offmarket' && (
            <>
              <div style={{ padding: '14px 16px', background: 'rgba(224, 165, 38,0.08)', border: '1px solid rgba(224, 165, 38,0.3)', borderRadius: 10, marginBottom: 14 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--gold)' }}>
                  No MLS listing found for that address
                </div>
                <div style={{ fontSize: 12, color: 'var(--gray-300)', marginTop: 6 }}>
                  Could be off-market, FSBO, or a slight address mismatch. If it's truly off-market,
                  add it as an off-market lead instead — that surface tracks self-sourced deals
                  and feeds into the same contract / blast pipeline.
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={() => setStep('address')} style={ghostBtn}>Try Again</button>
                <button onClick={goOffMarket} style={{ ...primaryBtn, flex: 2 }}>
                  Add as Off-Market Lead
                </button>
              </div>
            </>
          )}

          {step === 'saving' && <Spinner label="Saving + teaching the scorer…" />}

          {step === 'done' && result && (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '14px 16px', background: 'rgba(74,124,89,0.08)', border: '1px solid rgba(74,124,89,0.3)', borderRadius: 10, marginBottom: 14 }}>
                <div style={{ width: 32, height: 32, borderRadius: '50%', background: '#4A7C59', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, flexShrink: 0 }}>✓</div>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: '#4A7C59' }}>
                    Added to MLS Opportunities
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--gray-500)', marginTop: 2 }}>
                    Scorer will weight similar listings positively on next sync.
                  </div>
                </div>
              </div>

              {result.signals && (result.signals.keywords?.length || result.signals.phrases?.length || result.signals.note) && (
                <div style={{ background: 'var(--navy)', borderRadius: 8, padding: 14, marginBottom: 14 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>
                    What we learned ({result.signals.added} signal{result.signals.added === 1 ? '' : 's'} saved)
                  </div>
                  {result.signals.keywords?.length > 0 && (
                    <div style={{ marginBottom: 8 }}>
                      <div style={{ fontSize: 10, color: 'var(--gray-500)', marginBottom: 4 }}>KEYWORDS</div>
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                        {result.signals.keywords.map(k => (
                          <span key={k} style={chip}>{k}</span>
                        ))}
                      </div>
                    </div>
                  )}
                  {result.signals.phrases?.length > 0 && (
                    <div style={{ marginBottom: 8 }}>
                      <div style={{ fontSize: 10, color: 'var(--gray-500)', marginBottom: 4 }}>PHRASES (AI HINTS)</div>
                      {result.signals.phrases.map((p, i) => (
                        <div key={i} style={{ fontSize: 12, color: 'var(--gray-300)', marginBottom: 2 }}>• {p}</div>
                      ))}
                    </div>
                  )}
                  {result.signals.note && (
                    <div>
                      <div style={{ fontSize: 10, color: 'var(--gray-500)', marginBottom: 4 }}>SUMMARY</div>
                      <div style={{ fontSize: 12, color: 'var(--gray-300)', fontStyle: 'italic' }}>{result.signals.note}</div>
                    </div>
                  )}
                  {result.signals.explanation && (
                    <div style={{ fontSize: 10, color: 'var(--gray-500)', marginTop: 8 }}>{result.signals.explanation}</div>
                  )}
                </div>
              )}

              <button onClick={onClose} style={{ ...primaryBtn, width: '100%' }}>Done</button>
            </>
          )}

          {step === 'error' && (
            <>
              <div style={{ padding: '12px 14px', background: 'rgba(184,84,80,0.08)', border: '1px solid rgba(184,84,80,0.3)', borderRadius: 8, marginBottom: 14 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#B85450' }}>Something went wrong</div>
                <div style={{ fontSize: 12, color: 'var(--gray-300)', marginTop: 4 }}>{error}</div>
              </div>
              <button onClick={() => setStep('address')} style={{ ...primaryBtn, width: '100%' }}>Try Again</button>
            </>
          )}
        </div>
      </div>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

// ── Sub-components + styles ───────────────────────────────────────────────────

function Spinner({ label }) {
  return (
    <div style={{ padding: '60px 0', textAlign: 'center' }}>
      <div style={{ width: 36, height: 36, border: '3px solid var(--navy-border)', borderTopColor: 'var(--teal)', borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '0 auto 16px' }} />
      <div style={{ fontSize: 13, color: 'var(--gray-300)' }}>{label}</div>
    </div>
  );
}

function SuggestionBadge({ badge }) {
  const map = {
    filtered_out: { label: 'Filtered Out', color: '#B85450', bg: 'rgba(184,84,80,0.12)' },
    opportunity:  { label: 'Opportunity', color: '#4A7C59', bg: 'rgba(74,124,89,0.12)' },
    unscored:     { label: 'Unscored',    color: '#E0A526', bg: 'rgba(224, 165, 38,0.12)' },
    new_to_us:    { label: 'New (MLS)',   color: '#14504A', bg: 'rgba(20, 80, 74,0.12)' },
  };
  const b = map[badge] || map.unscored;
  return (
    <span style={{ flexShrink: 0, fontSize: 9, fontWeight: 700, padding: '2px 6px', borderRadius: 4, color: b.color, background: b.bg, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
      {b.label}
    </span>
  );
}

function Field({ label, required, children }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>
        {label}{required && <span style={{ color: '#B85450', marginLeft: 4 }}>*</span>}
      </div>
      {children}
    </div>
  );
}

const overlay = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)', zIndex: 9000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 };
const modalWrap = { background: 'var(--navy-card)', border: '1px solid var(--navy-border)', borderRadius: 16, width: '100%', maxWidth: 600, maxHeight: '90vh', display: 'flex', flexDirection: 'column' };
const header = { padding: '20px 28px 14px', borderBottom: '1px solid var(--navy-border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' };
const closeBtn = { background: 'none', border: 'none', color: 'var(--gray-500)', fontSize: 22, cursor: 'pointer', lineHeight: 1 };
const body = { flex: 1, overflowY: 'auto', padding: '18px 28px 24px' };

const input = { width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid var(--navy-border)', background: 'var(--navy)', color: 'var(--white)', fontSize: 12, outline: 'none', boxSizing: 'border-box' };

const primaryBtn = { flex: 1, padding: '10px 14px', borderRadius: 8, background: 'var(--teal)', border: '1px solid var(--teal)', color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer' };
const ghostBtn   = { flex: 1, padding: '10px 14px', borderRadius: 8, background: 'transparent', border: '1px solid var(--navy-border)', color: 'var(--gray-500)', fontSize: 13, fontWeight: 700, cursor: 'pointer' };

const hintBox = { padding: '10px 12px', background: 'rgba(20, 80, 74,0.06)', border: '1px solid rgba(20, 80, 74,0.2)', borderRadius: 8, fontSize: 11, color: 'var(--gray-300)', lineHeight: 1.5 };

const listingCard = { display: 'flex', gap: 12, padding: 12, background: 'var(--navy)', border: '1px solid var(--navy-border)', borderRadius: 10, marginBottom: 14 };

const chip = { padding: '3px 9px', borderRadius: 999, background: 'rgba(20, 80, 74,0.12)', border: '1px solid rgba(20, 80, 74,0.3)', color: 'var(--teal)', fontSize: 11, fontWeight: 600 };

const dropdown = {
  position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0,
  background: 'var(--navy-card)', border: '1px solid var(--navy-border)',
  borderRadius: 8, boxShadow: '0 6px 24px rgba(0,0,0,0.5)',
  maxHeight: 280, overflowY: 'auto', zIndex: 10,
};

const dropdownItem = {
  padding: '8px 12px',
  cursor: 'pointer',
  borderBottom: '1px solid rgba(232,227,218,0.06)',
};
